import { useEffect, ReactNode, useRef } from "react"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { toast } from "sonner"

interface OrganizationLoaderProps {
    children: ReactNode
}

/**
 * OrganizationLoader - Centralized organization loading component
 *
 * This component ensures organizations are loaded once when the user is authenticated.
 * It should be placed high in the component tree (in layouts) to avoid duplicate API calls.
 */
export function OrganizationLoader({ children }: OrganizationLoaderProps) {
    const { isAuthenticated, fetchUserProfile } = useAuth()
    const { loadOrganizations } = useOrganizationsService()
    const hasSynced = useRef(false)

    useEffect(() => {
        if (isAuthenticated && !hasSynced.current) {
            hasSynced.current = true
            loadOrganizations().catch((err) => {
                toast.error("Failed to load organisations:", err)
            })
            fetchUserProfile().catch((err) => {
                toast.error("Failed to refresh user profile:", err)
            })
        }
    }, [isAuthenticated, loadOrganizations, fetchUserProfile])

    return <>{children}</>
}
